import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from "react"

/**
 * URLState — the URL as application state.
 *
 * One mechanism gives us refresh-restore, Back/Forward and deep links:
 * components read their state FROM the URL and write it back TO the URL,
 * instead of keeping it in memory and mirroring it by hand.
 *
 *   const [tab, setTab]   = useUrlParam("tab", { default: "messages" })   // ?tab=pins
 *   const [{ teamId, channelId }, setPath] = useUrlPath("/account/teams/:teamId?/:channelId?")
 *   setPath({ teamId, channelId })          // → /account/teams/<t>/<c>   (replaceState by default)
 *
 * Grammar (kept in one place, see ROUTES below): the primary resource lives
 * in the PATH, transient view state in the QUERY. Device preferences
 * (sidebar collapsed, last surface) are NOT URL state — LocalUserPreferences.
 *
 * Writes go through history.replaceState (in-page state) or pushState
 * (a real "place" the user may want Back to return to). Both — plus any
 * pushState/replaceState issued elsewhere (vike navigate) and popstate —
 * notify subscribers, so every hook re-renders from the same source.
 *
 * SSR-safe: on the server (or before mount) hooks return defaults; pass
 * `initialUrl` (pageContext.urlPathname + search) to seed a server render.
 * A leading /<lang>/ prefix is preserved on writes and hidden from reads.
 */

// ── Route grammar ────────────────────────────────────────────────────────────
// Documented here so the whole app's URL shape is visible at a glance.
export const ROUTES = {
  chat:      "/account/chat/:conversationId?",          // ?tab=  ?thread=  ?discover=1
  teams:     "/account/teams/:teamId?/:channelId?",     // ?tab=  ?thread=
  account:   "/account/:section?",                      // profile, plans, invoices, contacts, …
}
// Query keys in use (any page): tab, thread, discover, prefs

// ── Core store ───────────────────────────────────────────────────────────────
const LANG_RE = /^\/([a-z]{2})(?=\/|$)/
const listeners = new Set()
let patched = false

function notify() { for (const fn of listeners) fn() }

// pushState/replaceState don't fire events; wrap them once so any writer
// (this module, vike's navigate, third parties) keeps subscribers in sync.
function ensurePatched() {
  if (patched || typeof window === "undefined") return
  patched = true
  for (const m of ["pushState", "replaceState"]) {
    const orig = window.history[m]
    window.history[m] = function (...args) {
      const r = orig.apply(this, args)
      // Fire after the URL actually changed (some routers batch).
      queueMicrotask(notify)
      return r
    }
  }
  window.addEventListener("popstate", notify)
}

/** Current URL split into what hooks need. */
export function readUrl(href) {
  if (!href && typeof window === "undefined") return { lang: "", pathname: "/", params: new URLSearchParams(), href: "" }
  const u = new URL(href || window.location.href, typeof window !== "undefined" ? window.location.origin : "http://localhost")
  const m = u.pathname.match(LANG_RE)
  const lang = m ? m[0] : ""
  const pathname = (m ? u.pathname.slice(lang.length) : u.pathname) || "/"
  return { lang, pathname, params: u.searchParams, href: u.pathname + u.search }
}

function subscribe(fn) {
  ensurePatched()
  listeners.add(fn)
  return () => listeners.delete(fn)
}

// Snapshot must be referentially stable between changes for useSyncExternalStore.
let lastHref = null
let lastSnap = null
function getSnapshot() {
  if (typeof window === "undefined") return getServerSnapshot()
  const href = window.location.pathname + window.location.search
  if (href !== lastHref) { lastHref = href; lastSnap = readUrl() }
  return lastSnap
}
let serverUrl = null
// Must be referentially stable — React calls getServerSnapshot repeatedly
// during hydration and treats a fresh object each time as an infinite loop.
const EMPTY_SERVER_URL = { lang: "", pathname: "/", params: new URLSearchParams(), href: "" }
function getServerSnapshot() {
  return serverUrl || EMPTY_SERVER_URL
}
/** Seed the server-side snapshot from pageContext (call in +onBeforeRender / Layout). */
export function setServerUrl(href) { serverUrl = href ? readUrl(href) : null }

/** Subscribe-able current URL. */
export function useUrl() {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
}

// ── Writing ──────────────────────────────────────────────────────────────────
/**
 * Write a new URL. `path` (lang-less) and/or `params` (object; null/undefined
 * value deletes the key; other keys untouched). history: "replace" | "push".
 */
export function writeUrl({ path, params } = {}, { history = "replace" } = {}) {
  if (typeof window === "undefined") return
  const cur = readUrl()
  const nextPath = path != null ? path : cur.pathname
  const sp = new URLSearchParams(cur.params)
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (v === null || v === undefined || v === false || v === "") sp.delete(k)
      else sp.set(k, String(v === true ? 1 : v))
    }
  }
  const qs = sp.toString()
  const href = `${cur.lang}${nextPath}${qs ? `?${qs}` : ""}`
  if (href === window.location.pathname + window.location.search) return
  window.history[history === "push" ? "pushState" : "replaceState"](window.history.state || {}, "", href)
}

// ── Path templates ───────────────────────────────────────────────────────────
/** Compile "/account/teams/:teamId?/:channelId?" → matcher + builder. */
export function compileTemplate(template) {
  const segs = template.split("/").filter(Boolean).map((s) => {
    const m = s.match(/^:(\w+)(\?)?$/)
    return m ? { name: m[1], optional: !!m[2] } : { literal: s }
  })
  const match = (pathname) => {
    const parts = pathname.split("/").filter(Boolean)
    const out = {}
    let i = 0
    for (const seg of segs) {
      const part = parts[i]
      if (seg.literal !== undefined) {
        if (part !== seg.literal) return null
        i++
        continue
      }
      if (part === undefined) {
        if (!seg.optional) return null
        out[seg.name] = null
        continue
      }
      out[seg.name] = decodeURIComponent(part)
      i++
    }
    if (i < parts.length) return null // trailing junk → no match
    return out
  }
  const build = (values = {}) => {
    const parts = []
    for (const seg of segs) {
      if (seg.literal !== undefined) { parts.push(seg.literal); continue }
      const v = values[seg.name]
      if (v === null || v === undefined || v === "") {
        if (!seg.optional) throw new Error(`URLState: missing "${seg.name}" for ${template}`)
        break // optional segments are positional — stop at the first empty one
      }
      parts.push(encodeURIComponent(String(v)))
    }
    return "/" + parts.join("/")
  }
  return { template, match, build }
}

const templateCache = new Map()
function getTemplate(t) {
  if (!templateCache.has(t)) templateCache.set(t, compileTemplate(t))
  return templateCache.get(t)
}

/**
 * Path params as state. Returns [values | null, setValues]. `values` is null
 * when the current path doesn't match the template. setValues(next, opts)
 * rebuilds the path from the template (keeps the query string).
 */
export function useUrlPath(template, { history = "replace" } = {}) {
  const url = useUrl()
  const t = getTemplate(template)
  const values = useMemo(() => t.match(url.pathname), [t, url.pathname])
  const set = useCallback((next, opts) => {
    writeUrl({ path: t.build(next) }, { history, ...(opts || {}) })
  }, [t, history])
  return [values, set]
}

/**
 * A query param as state — the useState drop-in. Returns [value, setValue].
 * Missing → `def`. setValue(null | "" | def) removes the key so defaults
 * never litter the URL. `parse`/`serialize` for non-string values.
 * `within` (a RegExp on the lang-less pathname): the param belongs to those
 * pages, and a write from anywhere else is dropped. A screen that picks a
 * value in an effect (the vendor portal choosing the organization) still
 * runs that effect for a moment after a link took the URL to another page,
 * and wrote its param onto the page it was leaving.
 */
export function useUrlParam(name, { default: def = null, history = "replace", parse, serialize, within } = {}) {
  const url = useUrl()
  const raw = url.params.get(name)
  const value = raw === null ? def : (parse ? parse(raw) : raw)
  const set = useCallback((next, opts) => {
    if (within && !within.test(readUrl().pathname)) return
    const v = typeof next === "function" ? next(readParam(name, { def, parse })) : next
    const out = v === def ? null : (serialize ? serialize(v) : v)
    writeUrl({ params: { [name]: out } }, { history, ...(opts || {}) })
  }, [name, def, history, parse, serialize, within])
  return [value, set]
}

function readParam(name, { def, parse }) {
  const raw = readUrl().params.get(name)
  return raw === null ? def : (parse ? parse(raw) : raw)
}

/** Boolean query flag: ?discover=1 ↔ true. */
export function useUrlFlag(name, opts = {}) {
  return useUrlParam(name, { ...opts, default: false, parse: (v) => v === "1" || v === "true", serialize: (v) => (v ? "1" : null) })
}

/** Non-hook read of a query param (event handlers, one-off consumers). */
export function getUrlParam(name) { return readUrl().params.get(name) }

/** Non-hook path match. */
export function matchPath(template, pathname = readUrl().pathname) { return getTemplate(template).match(pathname) }

/** Lang-less current href (path + query) — the thing to hand a sign-in redirect. */
export function currentHref() {
  if (typeof window === "undefined") return "/"
  const u = readUrl()
  return u.pathname + (u.params.toString() ? `?${u.params.toString()}` : "")
}

/**
 * Convenience: keep a piece of in-memory state mirrored into a query param
 * (write-only). Use when the state object can't itself live in the URL
 * (e.g. an open thread's root message) but its id should — pair with a
 * one-shot reader on mount to restore. Returns nothing.
 */
export function useUrlMirror(name, value, { history = "replace" } = {}) {
  useEffect(() => {
    writeUrl({ params: { [name]: value ?? null } }, { history })
  }, [name, value, history])
}

/** One-shot: the value a query param had when the component mounted. */
export function useInitialUrlParam(name) {
  const [v] = useState(() => (typeof window === "undefined" ? null : readUrl().params.get(name)))
  return v
}

export default {
  ROUTES, readUrl, writeUrl, compileTemplate, matchPath, currentHref, getUrlParam, setServerUrl,
  useUrl, useUrlPath, useUrlParam, useUrlFlag, useUrlMirror, useInitialUrlParam,
}
